import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  useSupport,
  myRole,
  sendAgentMessage,
  sendTyping,
  loadOlderMessages,
  runSessionAction,
} from "../../hooks/supportStore";
import { formatTime, photoUrl, showSupportError, STATUS_LABELS } from "./support-helpers";

function SupportMessage({ message, myId }) {
  if (message.sender_type === "system") {
    return <div className="cs-msg-system">{message.content}</div>;
  }

  const fromCustomer = message.sender_type === "customer";
  const fromBot = message.sender_type === "bot";
  const mine = message.sender_type === "agent" && message.sender_user_id === myId;
  const name = fromCustomer ? "Customer" : fromBot ? "Logi AI" : message.sender?.display_name || "Agent";

  const classes = [
    "cs-msg",
    fromCustomer || fromBot ? "cs-msg--in" : "cs-msg--out",
    message.is_internal_note ? "cs-msg--note" : "",
    message.is_ai_transcript ? "cs-msg--transcript" : "",
    message.failed ? "cs-msg--failed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <div className="cs-msg__meta">
        {message.is_internal_note ? (
          <span className="cs-msg__note-label">
            <i className="fa fa-lock" aria-hidden /> Internal note · {mine ? "You" : name}
          </span>
        ) : (
          <span>{mine ? "You" : name}</span>
        )}
        <span>{message.pending ? "Sending…" : message.failed ? "Not sent" : formatTime(message.createdAt)}</span>
      </div>
      <div className="cs-msg__body">{message.content}</div>
    </div>
  );
}

/** Banner shown when the agent is only viewing: offers the action that fits the session. */
function JoinBar({ session, role, onAction, busy }) {
  if (role) return null;
  if (session.status === "waiting")
    return (
      <div className="cs-joinbar">
        <span>This customer is waiting in the queue.</span>
        <button type="button" className="cs-btn cs-btn--primary" disabled={busy} onClick={() => onAction("claim")}>
          Accept chat
        </button>
      </div>
    );
  if (session.status === "active")
    return (
      <div className="cs-joinbar">
        <span>You are viewing this chat. Join to reply or help the agent.</span>
        <button type="button" className="cs-btn cs-btn--primary" disabled={busy} onClick={() => onAction("join")}>
          Join to assist
        </button>
      </div>
    );
  return (
    <div className="cs-joinbar cs-joinbar--muted">
      <span>
        This chat is {STATUS_LABELS[session.status]?.toLowerCase()}.
        {session.customer_online
          ? " The customer is still connected."
          : session.customer_email
            ? ` Follow up by email: ${session.customer_email}`
            : ""}
      </span>
      {session.customer_online && (
        <button type="button" className="cs-btn cs-btn--primary" disabled={busy} onClick={() => onAction("reopen")}>
          Reopen chat
        </button>
      )}
    </div>
  );
}

function SupportChat({ sessionId, onBack, onShowDetails }) {
  const support = useSupport();
  const session = support.sessions[sessionId];
  const messages = support.messages[sessionId];
  const myId = support.me?.id;
  const role = myRole(session, myId);
  const [draft, setDraft] = useState("");
  const [noteMode, setNoteMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const stickToBottom = useRef(true);
  const prevCount = useRef(0);

  useEffect(() => {
    setDraft("");
    setNoteMode(false);
    stickToBottom.current = true;
  }, [sessionId]);

  // Keep the view pinned to the newest message unless the agent scrolled up.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !messages) return;
    if (stickToBottom.current || messages.length - prevCount.current === 1) el.scrollTop = el.scrollHeight;
    prevCount.current = messages.length;
  }, [messages, support.customerTyping[sessionId]]);

  if (!session) return <div className="cs-chat cs-chat--empty">Loading chat…</div>;

  const canReply = session.status === "active" && Boolean(role);
  // Notes are allowed on any chat, including missed ones, so the team can leave follow-ups.
  const canSend = noteMode || canReply;

  const doAction = async (action) => {
    setBusy(true);
    const res = await runSessionAction(action, sessionId);
    setBusy(false);
    if (!res.success) showSupportError(res.message);
  };

  const submit = async () => {
    const text = draft.trim();
    if (!text || !canSend) return;
    setDraft("");
    stickToBottom.current = true;
    const res = await sendAgentMessage(sessionId, text, noteMode);
    if (!res.success && res.message) showSupportError(res.message);
  };

  const transcript = (messages || []).filter((m) => m.is_ai_transcript);
  const live = (messages || []).filter((m) => !m.is_ai_transcript);

  return (
    <section className="cs-chat" aria-label={`Chat with ${session.customer_name}`}>
      <header className="cs-chat__head">
        {onBack && (
          <button type="button" className="cs-icon-btn" onClick={onBack} aria-label="Back to inbox">
            <i className="fa fa-arrow-left" aria-hidden />
          </button>
        )}
        <div className="cs-chat__title">
          <strong>{session.customer_name}</strong>
          <span>
            <span className={`cs-pill cs-pill--${session.status}`}>{STATUS_LABELS[session.status]}</span>
            {session.ticket_id && <span className="cs-chat__ticket">{session.ticket_id}</span>}
            <span className={`cs-online${session.customer_online ? " cs-online--on" : ""}`}>
              {session.customer_online ? "Customer online" : "Customer offline"}
            </span>
          </span>
        </div>
        <div className="cs-chat__agents">
          {(session.agents || []).map((a) => (
            <img
              key={a.user_id}
              src={photoUrl(a.profile_photo)}
              alt={a.display_name}
              title={`${a.display_name}${a.role === "owner" ? " (owner)" : ""}`}
              className={`cs-avatar${a.role === "owner" ? " cs-avatar--owner" : ""}`}
            />
          ))}
        </div>
        {onShowDetails && (
          <button type="button" className="cs-icon-btn" onClick={onShowDetails} aria-label="Customer details">
            <i className="fa fa-info-circle" aria-hidden />
          </button>
        )}
      </header>

      <JoinBar session={session} role={role} onAction={doAction} busy={busy} />

      <div
        className="cs-chat__messages"
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
          if (el.scrollTop < 40 && support.hasMore[sessionId]) loadOlderMessages(sessionId);
        }}
      >
        {!messages && <p className="cs-empty">Loading messages…</p>}
        {transcript.length > 0 && (
          <details className="cs-transcript">
            <summary>Conversation with Logi AI ({transcript.length} messages)</summary>
            {transcript.map((m) => (
              <SupportMessage key={m.id} message={m} myId={myId} />
            ))}
          </details>
        )}
        {live.map((m) => (
          <SupportMessage key={m.id} message={m} myId={myId} />
        ))}
        {support.customerTyping[sessionId] && <div className="cs-typing">Customer is typing…</div>}
        {support.agentTyping[sessionId] && (
          <div className="cs-typing">{support.agentTyping[sessionId]} is typing…</div>
        )}
      </div>

      <form
        className={`cs-composer${noteMode ? " cs-composer--note" : ""}`}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="cs-composer__modes" role="tablist" aria-label="Message type">
          <button
            type="button"
            role="tab"
            aria-selected={!noteMode}
            className={!noteMode ? "is-active" : ""}
            onClick={() => setNoteMode(false)}
          >
            Reply to customer
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={noteMode}
            className={noteMode ? "is-active" : ""}
            onClick={() => setNoteMode(true)}
          >
            <i className="fa fa-lock" aria-hidden /> Internal note
          </button>
        </div>
        <div className="cs-composer__row">
          <textarea
            rows={2}
            maxLength={4000}
            value={draft}
            disabled={!canSend}
            placeholder={
              noteMode
                ? "Note for the team — the customer will not see this"
                : canReply
                  ? "Type a reply…"
                  : role
                    ? "This chat is closed."
                    : "Accept or join the chat to reply."
            }
            onChange={(e) => {
              setDraft(e.target.value);
              if (!noteMode && canReply) sendTyping(sessionId, Boolean(e.target.value));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button type="submit" className="cs-btn cs-btn--primary" disabled={!canSend || !draft.trim()}>
            {noteMode ? "Add note" : "Send"}
          </button>
        </div>
      </form>
    </section>
  );
}

export default SupportChat;
